import { React, useEffect, useState } from 'react';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';

import { Alerta } from '../../components/alerta';
import circularProgress from '../../components/circularProgress';
import PageHeader from '../../components/pageHeader';
import { formatDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/chamado.css';

const STATUS_OPTIONS = ['NÃO INICIADO', 'EM ATENDIMENTO', 'FINALIZADO', 'CANCELADO'];

const getField = (chamado, keys) => {
    for (const key of keys) {
        if (chamado?.[key] !== undefined && chamado?.[key] !== null) {
            return chamado[key];
        }
    }

    return '';
};

const getStatusOptions = (status) => {
    if (!status || STATUS_OPTIONS.includes(status)) {
        return STATUS_OPTIONS;
    }

    return [status, ...STATUS_OPTIONS];
};

const AtendimentoChamados = () => {
    const [chamados, setChamados] = useState([]);
    const [statusPorChamado, setStatusPorChamado] = useState({});
    const [comentarioPorChamado, setComentarioPorChamado] = useState({});
    const [savingChamadoId, setSavingChamadoId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    const handleAlerta = (response) => {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    };

    useEffect(() => {
        async function fetchChamados () {
            const response = await fetchFlask('listarChamadosAtendente');

            if (Array.isArray(response)) {
                setChamados(response);
                setStatusPorChamado(response.reduce((acc, chamado) => {
                    acc[getField(chamado, ['id'])] = getField(chamado, ['status']);
                    return acc;
                }, {}));
            } else if (Array.isArray(response.chamados)) {
                setChamados(response.chamados);
                setStatusPorChamado(response.chamados.reduce((acc, chamado) => {
                    acc[getField(chamado, ['id'])] = getField(chamado, ['status']);
                    return acc;
                }, {}));
            } else {
                handleAlerta({
                    message: response.message || 'Não foi possível carregar os chamados.',
                    status: response.status || 'error'
                });
            }

            setIsLoading(false);
        };

        fetchChamados();
    }, []);

    const handleStatusChange = (chamadoId, nextStatus) => {
        setStatusPorChamado((currentStatus) => ({
            ...currentStatus,
            [chamadoId]: nextStatus
        }));
    };

    const handleComentarioChange = (chamadoId, comentario) => {
        setComentarioPorChamado((currentComentarios) => ({
            ...currentComentarios,
            [chamadoId]: comentario
        }));
    };

    async function submitChamado (chamadoId) {
        setSavingChamadoId(chamadoId);

        const submittedStatus = statusPorChamado[chamadoId];
        const submittedComentario = comentarioPorChamado[chamadoId] || '';
        const body = JSON.stringify({
            id: chamadoId,
            status: submittedStatus,
            comentario: submittedComentario
        });

        try {
            const response = await fetchFlask('atualizarStatusChamado', body);
            handleAlerta(response);

            if (response.status === 'success') {
                setChamados((currentChamados) => currentChamados.map((chamado) => {
                    if (getField(chamado, ['id']) !== chamadoId) {
                        return chamado;
                    }

                    return {
                        ...chamado,
                        status: submittedStatus
                    };
                }));
                setComentarioPorChamado((currentComentarios) => ({
                    ...currentComentarios,
                    [chamadoId]: ''
                }));
            }
        } finally {
            setSavingChamadoId(null);
        }
    };

    return (
        <Box className='mainBox chamado-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader icon={SupportAgentRoundedIcon}>
                Atendimento Chamados
            </PageHeader>

            {isLoading && circularProgress()}

            {!isLoading && chamados.length === 0 && (
                <Typography color='text.secondary' sx={{ mt: 2 }}>
                    Nenhum chamado encontrado.
                </Typography>
            )}

            {!isLoading && chamados.length > 0 && (
                <TableContainer component={Paper} className='chamado-table'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Ações</TableCell>
                                <TableCell>Usuário</TableCell>
                                <TableCell>Departamento</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell>Título</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Comentário</TableCell>
                                <TableCell>Data Criação</TableCell>
                                <TableCell>Última Alteração</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {chamados.map((chamado, index) => {
                                const chamadoId = getField(chamado, ['id']);
                                const chamadoStatus = statusPorChamado[chamadoId] || getField(chamado, ['status']);
                                const comentario = comentarioPorChamado[chamadoId] || '';
                                const hasChanges = chamadoStatus !== getField(chamado, ['status']) || comentario.length > 0;
                                const isSaving = savingChamadoId === chamadoId;

                                return (
                                    <TableRow key={chamadoId || index}>
                                        <TableCell>{chamadoId}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant='contained'
                                                size='small'
                                                startIcon={<SaveRoundedIcon />}
                                                className='chamado-save-button'
                                                disabled={!chamadoId || !hasChanges || isSaving}
                                                onClick={() => submitChamado(chamadoId)}
                                            >
                                                Salvar
                                            </Button>
                                        </TableCell>
                                        <TableCell>{getField(chamado, ['usuario', 'nomeUsuario', 'nome_usuario', 'userName', 'solicitante'])}</TableCell>
                                        <TableCell>{getField(chamado, ['departamento'])}</TableCell>
                                        <TableCell>{getField(chamado, ['categoria'])}</TableCell>
                                        <TableCell>{getField(chamado, ['titulo'])}</TableCell>
                                        <TableCell className='chamado-table-description'>
                                            {getField(chamado, ['descricao'])}
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                select
                                                size='small'
                                                value={chamadoStatus}
                                                onChange={(e) => handleStatusChange(chamadoId, e.target.value)}
                                                disabled={!chamadoId || isSaving}
                                                className='chamado-status-field'
                                            >
                                                {getStatusOptions(chamadoStatus).map((statusOption) => (
                                                    <MenuItem key={statusOption} value={statusOption}>
                                                        {statusOption}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                multiline
                                                minRows={2}
                                                maxRows={4}
                                                size='small'
                                                value={comentario}
                                                onChange={(e) => handleComentarioChange(chamadoId, e.target.value)}
                                                disabled={!chamadoId || isSaving}
                                                placeholder='Comentário opcional'
                                                className='chamado-comment-field'
                                                inputProps={{ 'aria-label': `Comentário do chamado ${chamadoId}` }}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(getField(chamado, ['data_criacao']))}</TableCell>
                                        <TableCell>{formatDate(getField(chamado, ['ultima_alteracao']))}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default AtendimentoChamados;
